import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, RefreshCw, Trash2, Phone, Inbox } from 'lucide-react';
import { IMAGES, SITE } from '../data/site';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [leads, setLeads] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('beh_admin_token');

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    try {
      const me = await axios.get(`${API}/auth/me`, authHeaders());
      setUser(me.data);
      const res = await axios.get(`${API}/admin/leads`, authHeaders());
      setLeads(res.data.leads);
    } catch {
      localStorage.removeItem('beh_admin_token');
      navigate('/admin/login');
    }
  }, [authHeaders, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    load();
  }, [token, load, navigate]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, authHeaders());
      setLeads(leads.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error('Could not update lead');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, authHeaders());
      setLeads(leads.filter((l) => l.id !== id));
      toast.success('Lead deleted');
    } catch {
      toast.error('Could not delete lead');
    }
  };

  const logout = () => {
    localStorage.removeItem('beh_admin_token');
    navigate('/admin/login');
  };

  const statusColor = { new: 'bg-brand-soft text-brand', contacted: 'bg-amber-100 text-amberwarm', closed: 'bg-emerald-100 text-trustgreen' };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt={`${SITE.name} admin`} className="h-9 w-auto rounded" />
            <div>
              <h1 className="font-display text-lg font-bold text-navy">Leads Dashboard</h1>
              {user && <p className="text-xs text-slate-500">{user.email}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-navy-light">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold text-navy">Website Leads</h2>
            <p className="text-sm text-slate-500" data-testid="admin-lead-count">
              {leads ? `${leads.length} total · ${leads.filter((l) => l.status === 'new').length} new` : 'Loading...'}
            </p>
          </div>
          <Link to="/" className="text-sm font-semibold text-brand hover:text-blue-700" data-testid="admin-view-site">View website</Link>
        </div>

        {leads === null ? (
          <p className="py-20 text-center text-slate-400">Loading leads...</p>
        ) : leads.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-empty-state">
            <Inbox className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-display text-lg font-bold text-navy">No leads yet</p>
            <p className="text-sm text-slate-500">New website form submissions will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => (
              <article key={lead.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`lead-card-${lead.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-lg font-bold text-navy">{lead.name}</h3>
                      <span className={`rounded-full px-3 py-0.5 font-mono text-xs font-bold uppercase tracking-wider ${statusColor[lead.status] || statusColor.new}`} data-testid={`lead-status-${lead.id}`}>
                        {lead.status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {new Date(lead.created_at).toLocaleString()} · {lead.service}{lead.city ? ` · ${lead.city}` : ''}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${lead.formType === 'install' ? 'bg-amber-100 text-amberwarm' : 'bg-brand-soft text-brand'}`}>
                        {lead.formType === 'install' ? 'Install Estimate' : 'Service Call'}
                      </span>
                      {[lead.urgency, lead.systemAge, lead.propertyType, lead.homeSize, lead.financing].filter(Boolean).map((chip) => (
                        <span key={chip} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600">{chip}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`tel:${lead.phone}`} data-testid={`lead-call-${lead.id}`} className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white hover:bg-brand-dark">
                      <Phone className="h-4 w-4" /> {lead.phone}
                    </a>
                    <button onClick={() => remove(lead.id)} data-testid={`lead-delete-${lead.id}`} aria-label="Delete lead" className="rounded-full border border-slate-200 p-2 text-slate-400 transition-colors hover:border-red-300 hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {lead.email && <p className="mt-2 text-sm text-slate-600">Email: <a className="text-brand" href={`mailto:${lead.email}`}>{lead.email}</a></p>}
                {lead.message && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">{lead.message}</p>}
                <div className="mt-4 flex gap-2">
                  {['new', 'contacted', 'closed'].map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(lead.id, s)}
                      data-testid={`lead-mark-${s}-${lead.id}`}
                      className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                        lead.status === s ? 'bg-navy text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
